import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../identity/types';
import { compensationGateway } from '../../compensation';
import { compensationPayTypeLabels } from '../../compensation/types';
import { HelpTooltip } from '../../../shared/components/HelpTooltip';
import { AddDisciplineDialog } from '../../healthcare-rates/components/AddDisciplineDialog';
import { DisciplineField } from '../../healthcare-rates/components/DisciplineCombobox';
import { useHealthcareCapabilities } from '../../healthcare-rates/useHealthcareCapabilities';
import { useCompanyIndustry } from '../../healthcare-rates/useCompanyIndustry';
import { payBasisFromPayType, payTypeFromPayBasis } from '../../healthcare-rates/wireMapping';
import {
  BASE_PAY_BASES, REIMBURSEMENT_SEPARATE_SHORT, TERM_HELP, payBasisLabels, rateHelp as rateBasisHelp, rateLabels, type Discipline,
} from '../../healthcare-rates/types';
import { jobRoleGateway } from '../../job-roles';
import { basisForPayType, basisUnit, compareRateToBand, formatBandRange, formatHours, formatMoney } from '../../job-roles/payBand';
import {
  SYNTHETIC_DATA_LABEL, normalizeJobRoleContent,
  type BenchmarkResult, type BenchmarkStatus, type PayBand,
} from '../../job-roles/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { peopleGateway } from '../index';
import {
  EMPLOYEE_PROFILE_STEPS,
  employmentTypeLabels,
  type CompanyWorkLocationOption,
  type DepartmentOption,
  type JobTitleOption,
} from '../types';
import { AddDepartmentDialog } from './AddDepartmentDialog';
import { AddWorkLocationDialog } from './AddWorkLocationDialog';
import { locationsAfterAdd } from './locationsAfterAdd';
import type { WorkLocationResponse } from '../../business-profile-setup/types';
import { CreateRoleDialog } from './CreateRoleDialog';
import { RoleCombobox } from './RoleCombobox';
import './add-employee-dialog.css';

// The approved "Add Employee" dialog (Figma frame 49). Opened from the People list's "Add employee" action.
// "Save & Continue" creates the employee through the EXISTING create API, then records the work location
// (existing work-location endpoint) and the primary pay rate (existing Compensation work-rate-assignment
// endpoint), then continues into the Employee Profile setup. There is no single atomic endpoint for the three
// writes, so they run in order and a failure after the employee exists is reported honestly: the created
// employee id is kept in this component, the fields lock, and "Retry" only repeats the steps that have not
// succeeded yet — so a retry can never create a second employee. (The server has no idempotency key.)
//
// Role = the employee's JOB role (jobTitleId): what the person does, never system access. It is a type-ahead over the company's
// ACTIVE roles (title and department), with a "Create new role" option that opens a small dialog on top of this one, so nothing
// already typed is lost. Choosing a role may SUGGEST Department, Employment Type and Pay Type, but only into a field that is still
// empty, and it never touches the Rate or anything about access. Discipline is a DIFFERENT field and has no backend
// field/endpoint yet, so it is shown disabled and is never derived from Role. Error text never echoes a submitted value.
//
// "Invite Employee" (EPN-216) runs the same three writes, then asks Identity to email the employee an invitation
// (`POST /api/employee-invitations`, which takes only the employee id). The admin supplies the work-agreement
// fields; the employee supplies their own personal details after accepting, so Phone Number and Date of Birth are
// optional on this path (validated only if typed). A failed invitation keeps the created employee and reuses one
// idempotency key, so retrying never creates a second employee or sends a second email.

export interface AddEmployeeDialogProps {
  onClose: () => void;
  /** Called once an invitation has been sent, so the People list can refresh. */
  onInvited?: () => void;
}

type Mode = 'save' | 'invite';

type FieldKey =
  | 'employeeNumber' | 'jobTitleId' | 'firstName' | 'lastName' | 'email' | 'phone' | 'dateOfBirth' | 'hireDate'
  | 'departmentId' | 'employmentType' | 'workLocationId' | 'payType' | 'rate';

type Errors = Partial<Record<FieldKey, string>>;

// The six pay bases (Hourly, Per Visit, Per Shift, Daily, Salary, Per Unit/Case). The legacy Commission, Tips and Executive bonus values stay valid
// in data but are not offered for a new employee.
const PAY_TYPES = BASE_PAY_BASES.map((basis) => ({ value: payTypeFromPayBasis(basis), label: payBasisLabels[basis] }));
const PAY_TYPE_LABELS: Record<number, string> = Object.fromEntries(PAY_TYPES.map((t) => [t.value, t.label]));
const EMPLOYMENT_TYPES = Object.entries(employmentTypeLabels).map(([value, label]) => ({ value: Number(value), label }));
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
const DUPLICATE_NUMBER = /employee number is already in use/i;

/** The key of the label that equals `text` ignoring case and spacing, or null. A role states its typical employment and pay type as free
 * text; only a clear match with this form's own lists is ever suggested. */
function enumKeyForLabel(labels: Record<number, string>, text: string | null | undefined): number | null {
  const wanted = (text ?? '').trim().toLowerCase();
  if (!wanted) return null;
  const hit = Object.entries(labels).find(([, label]) => label.toLowerCase() === wanted);
  return hit ? Number(hit[0]) : null;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

/** Whole dollars and at most two decimals (the column is decimal(18,2)); null when it is not that shape. */
function parseRate(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** E.164 with the country code. Spaces, dashes, dots and parentheses are tolerated and removed. */
function normalizePhone(raw: string): string | null {
  const t = raw.replace(/[\s\-().]/g, '');
  return /^\+[1-9]\d{6,14}$/.test(t) ? t : null;
}

interface Progress { employeeId: string | null; locationSaved: boolean; rateSaved: boolean; invited: boolean }

const ADD_DEPARTMENT = '__add_department__';
const ADD_WORK_LOCATION = '__add_work_location__';

export function AddEmployeeDialog({ onClose, onInvited }: AddEmployeeDialogProps) {
  const navigate = useNavigate();
  const uid = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(typeof document === 'undefined' ? null : document.activeElement);

  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [addDepartmentOpen, setAddDepartmentOpen] = useState(false);
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [locationNote, setLocationNote] = useState('');
  const [jobTitles, setJobTitles] = useState<JobTitleOption[]>([]);
  const [locations, setLocations] = useState<CompanyWorkLocationOption[]>([]);
  const [listsError, setListsError] = useState<string | null>(null);

  const [employeeNumber, setEmployeeNumber] = useState('');
  const [jobTitleId, setJobTitleId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [workLocationId, setWorkLocationId] = useState('');
  const [payType, setPayType] = useState('');
  const [rate, setRate] = useState('');
  // Healthcare disciplines: the primary discipline is saved by Employees with the employee (`disciplineId`); it is optional.
  const [disciplineId, setDisciplineId] = useState('');
  const [disciplineName, setDisciplineName] = useState('');
  const [addDisciplineOpen, setAddDisciplineOpen] = useState(false);
  const [addDisciplineTyped, setAddDisciplineTyped] = useState('');
  const [disciplineNote, setDisciplineNote] = useState('');
  const caps = useHealthcareCapabilities();
  const { defaultTags: industryTags, label: industryLabel, isHealthcare } = useCompanyIndustry();
  const [isNewHire, setIsNewHire] = useState(false);
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [createRoleTitle, setCreateRoleTitle] = useState('');
  const [suggestionNote, setSuggestionNote] = useState('');
  // The role's employer pay band and benchmark hint (PR 2). Each result remembers which role (and view) it belongs to, so a slow answer for
  // a role that is no longer chosen is never shown. Information only: nothing here fills or changes the Rate.
  const [bandState, setBandState] = useState<{ roleId: string; band: PayBand | null } | null>(null);
  const [benchStatus, setBenchStatus] = useState<{ roleId: string; status: BenchmarkStatus } | null>(null);
  const [benchState, setBenchState] = useState<{ roleId: string; basis: 'Hourly' | 'Annual'; result: BenchmarkResult } | null>(null);

  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<Progress>({ employeeId: null, locationSaved: false, rateSaved: false, invited: false });
  const progressRef = useRef(progress);
  const modeRef = useRef<Mode>('save');
  const inviteKeyRef = useRef(crypto.randomUUID());
  const [lastMode, setLastMode] = useState<Mode>('save');

  // The values as they are NOW, so an asynchronous role suggestion never overwrites something typed while it was loading.
  const latest = useRef({ departmentId, employmentType, payType, jobTitleId });
  useEffect(() => {
    latest.current = { departmentId, employmentType, payType, jobTitleId };
  });

  const created = progress.employeeId !== null;
  const partial = created && !(progress.locationSaved && progress.rateSaved && (lastMode !== 'invite' || progress.invited));

  useEffect(() => {
    let cancelled = false;
    void Promise.all([peopleGateway.listDepartments(), peopleGateway.listJobTitles(), peopleGateway.listCompanyWorkLocations()])
      .then(([d, j, l]) => {
        if (cancelled) return;
        setDepartments(d.filter((x) => x.isActive));
        setJobTitles(j.filter((x) => x.isActive));
        setLocations(l.filter((x) => x.status === 'Active'));
      })
      .catch(() => {
        if (!cancelled) setListsError('Could not load the department, role and work location lists. Close this dialog and try again.');
      });
    return () => { cancelled = true; };
  }, []);

  // Focus moves into the dialog on open and back to the opener on close.
  useEffect(() => {
    const opener = openerRef.current;
    dialogRef.current?.querySelector<HTMLElement>('input, select')?.focus();
    return () => { if (opener instanceof HTMLElement) opener.focus(); };
  }, []);

  const rolesForDepartment = useMemo(
    () => jobTitles.filter((j) => !departmentId || !j.departmentId || j.departmentId === departmentId),
    [jobTitles, departmentId],
  );

  // The employer pay band of the chosen role (needs roles.read only). A failed read just means no line is shown.
  useEffect(() => {
    if (!jobTitleId) return;
    const controller = new AbortController();
    jobRoleGateway
      .getPayBand(jobTitleId, controller.signal)
      .then((band) => { if (!controller.signal.aborted) setBandState({ roleId: jobTitleId, band }); })
      .catch(() => { if (!controller.signal.aborted) setBandState({ roleId: jobTitleId, band: null }); });
    return () => controller.abort();
  }, [jobTitleId]);

  // The one-line market hint: only when the role's benchmarkStatus says it is permitted AND available. National, shown the way the pay type is stated.
  const hintBasis: 'Hourly' | 'Annual' = payType === '1' ? 'Hourly' : 'Annual';
  // A market median is an hourly or annual figure: it says nothing about a per-visit, per-shift, daily or per-unit rate, so none is shown for those.
  const quantityPriced = ['6', '7', '8', '9'].includes(payType);
  const hintAvailable = benchStatus?.roleId === jobTitleId && benchStatus.status === 'Available' && !quantityPriced;
  useEffect(() => {
    if (!jobTitleId || !hintAvailable) return;
    const controller = new AbortController();
    jobRoleGateway
      // `quiet`: this is an optional hint. If the plan does not include benchmarks (403 SUBSCRIPTION_UPGRADE_REQUIRED), or the lookup is refused
      // or missing (403, 404, 503), nothing extra is shown and no app-wide notice is raised: Add Employee works exactly as it does without a hint.
      .getBenchmarks(jobTitleId, { geographyType: 'National', basis: hintBasis }, controller.signal, { quiet: true })
      .then((result) => { if (!controller.signal.aborted) setBenchState({ roleId: jobTitleId, basis: hintBasis, result }); })
      .catch(() => undefined);
    return () => controller.abort();
  }, [jobTitleId, hintAvailable, hintBasis]);

  /** Choosing a role may fill Department, Employment Type and Pay Type, only where the person has not already chosen one. It never sets the Rate. */
  async function suggestFromRole(role: JobTitleOption) {
    const applied: string[] = [];
    if (!latest.current.departmentId && role.departmentId) {
      const dept = departments.find((d) => d.id === role.departmentId);
      if (dept) {
        setDepartmentId(dept.id);
        clear('departmentId');
        applied.push(`Department ${dept.name}`);
      }
    }
    try {
      const detail = await jobRoleGateway.getRole(role.id);
      if (latest.current.jobTitleId === role.id) {
        setBenchStatus({ roleId: role.id, status: detail.benchmarkStatus });
        const guidance = normalizeJobRoleContent(detail.content).employmentGuidance;
        // Free text on the role, matched to this form's lists by label; no clear match means no suggestion.
        const typeKey = enumKeyForLabel(employmentTypeLabels, guidance.typicalEmploymentType);
        if (!latest.current.employmentType && typeKey !== null) {
          setEmploymentType(String(typeKey));
          clear('employmentType');
          applied.push(`Employment Type ${employmentTypeLabels[typeKey]}`);
        }
        const payKey = enumKeyForLabel(PAY_TYPE_LABELS, guidance.typicalPayType);
        if (!latest.current.payType && payKey !== null) {
          setPayType(String(payKey));
          clear('payType');
          applied.push(`Pay Type ${PAY_TYPE_LABELS[payKey]}`);
        }
      }
    } catch {
      // The person may not be allowed to read role details (roles.read), or the service may be down. The role is still chosen.
    }
    if (latest.current.jobTitleId === role.id) {
      setSuggestionNote(applied.length > 0 ? `Suggested from this role: ${applied.join(', ')}. You can change these. The Rate is never filled in.` : '');
    }
  }

  function validate(mode: Mode): Errors {
    const e: Errors = {};
    if (!employeeNumber.trim()) e.employeeNumber = 'Enter an Employee ID.';
    if (!jobTitleId) e.jobTitleId = 'Choose a Role.';
    if (!firstName.trim()) e.firstName = 'Enter a first name.';
    if (!lastName.trim()) e.lastName = 'Enter a last name.';
    if (!email.trim()) e.email = 'Enter an email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Enter a valid email address, like name@example.com.';
    if (!phone.trim()) { if (mode === 'save') e.phone = 'Enter a phone number.'; }
    else if (!normalizePhone(phone)) e.phone = 'Enter the phone number with its country code, like +1 555 555 0123.';
    if (!dateOfBirth) { if (mode === 'save') e.dateOfBirth = 'Enter the date of birth.'; }
    else if (dateOfBirth >= todayIso()) e.dateOfBirth = 'The date of birth must be in the past.';
    if (!hireDate) e.hireDate = 'Enter the hiring date.';
    else if (dateOfBirth && hireDate < dateOfBirth) e.hireDate = 'The hiring date cannot be before the date of birth.';
    if (!departmentId) e.departmentId = 'Choose a Department.';
    if (!employmentType) e.employmentType = 'Choose an Employment Type.';
    if (!workLocationId) e.workLocationId = 'Choose a Work Location.';
    if (!payType) e.payType = 'Choose a Pay Type.';
    const amount = parseRate(rate);
    if (!rate.trim()) e.rate = 'Enter the rate.';
    else if (amount === null) e.rate = 'Enter the rate as a number with up to 2 decimals.';
    else if (amount <= 0) e.rate = 'The rate must be greater than zero.';
    return e;
  }

  function track(next: Progress) {
    progressRef.current = next;
    setProgress(next);
  }

  function focusField(key: string) {
    dialogRef.current?.querySelector<HTMLElement>(`[data-field="${key}"]`)?.focus();
  }

  async function submit(mode: Mode, e?: FormEvent) {
    e?.preventDefault();
    if (saving) return;
    modeRef.current = mode;
    setLastMode(mode);
    setFormError(null);
    // Once the employee exists the fields are locked and already validated; a retry only repeats failed steps.
    if (!progressRef.current.employeeId) {
      const found = validate(mode);
      setErrors(found);
      const first = Object.keys(found)[0];
      if (first) {
        focusField(first);
        return;
      }
    }
    setSaving(true);
    try {
      let current = progressRef.current;
      if (!current.employeeId) {
        try {
          const createdEmployee = await peopleGateway.createEmployee({
            employeeNumber: employeeNumber.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            dateOfBirth: dateOfBirth || null,
            hireDate,
            ssnLast4: null,
            departmentId,
            jobTitleId,
            phone: phone.trim() ? normalizePhone(phone) : null,
            isNewHire,
            employmentType: Number(employmentType),
            ...(disciplineId ? { disciplineId } : {}),
          });
          current = { ...current, employeeId: createdEmployee.id };
          track(current);
        } catch (err) {
          if (err instanceof ApiError && DUPLICATE_NUMBER.test(err.message)) {
            setErrors({ employeeNumber: 'That Employee ID is already in use in this company. Enter a different one.' });
            focusField('employeeNumber');
          } else {
            setFormError(friendlyErrorMessage(err, 'Could not create this employee.'));
          }
          return;
        }
      }
      const employeeId = current.employeeId!;
      const failures: string[] = [];
      if (!current.locationSaved) {
        try {
          await peopleGateway.saveEmployeeWorkLocation(employeeId, {
            primaryWorkLocationId: workLocationId,
            effectiveDate: hireDate,
            reason: null,
            workArrangement: null,
            isHomeAddressAlsoWorkLocation: null,
            allowedWorkLocationIds: [],
          });
          current = { ...current, locationSaved: true };
          track(current);
        } catch {
          failures.push('work location');
        }
      }
      if (!current.rateSaved) {
        try {
          await compensationGateway.createWorkRateAssignment(employeeId, {
            workRateId: null,
            label: compensationPayTypeLabels[Number(payType)] ?? 'Pay rate',
            payType: Number(payType),
            rate: parseRate(rate)!,
            isPrimary: true,
            effectiveStartDate: hireDate,
          });
          current = { ...current, rateSaved: true };
          track(current);
        } catch {
          failures.push('pay rate');
        }
      }
      if (failures.length > 0) {
        setFormError(
          `The employee was created, but the ${failures.join(' and ')} could not be saved. Choose Retry to save ${failures.length > 1 ? 'them' : 'it'} again, ` +
          'or continue to the profile to finish setup there. Retrying will not create a second employee.' +
          (mode === 'invite' ? ' No invitation has been sent yet.' : ''),
        );
        return;
      }
      if (mode === 'invite') {
        if (!current.invited) {
          try {
            await peopleGateway.createEmployeeInvitation(employeeId, inviteKeyRef.current);
            current = { ...current, invited: true };
            track(current);
            onInvited?.();
          } catch (err) {
            setFormError(
              `${friendlyErrorMessage(err, 'The invitation could not be sent.')} The employee was created. ` +
              'Choose Retry to send the invitation again; it will not create a second employee.',
            );
          }
        }
        return;
      }
      navigate(`/people/${employeeId}/${EMPLOYEE_PROFILE_STEPS[0].key}`);
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!saving) onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const titleId = `${uid}-title`;
  const locked = created || saving;
  const invitedDone = progress.invited;

  // ---- employer pay band line, benchmark hint and the out-of-band note (information only: never fills, blocks or changes the Rate) ----
  const band = jobTitleId && bandState?.roleId === jobTitleId ? bandState.band : null;
  const comparison = band ? compareRateToBand(parseRate(rate) ?? Number.NaN, basisForPayType(payType ? Number(payType) : null), band) : null;
  const hintResult = jobTitleId && benchState?.roleId === jobTitleId && benchState.basis === hintBasis ? benchState.result : null;
  const hintSources = (hintResult?.status === 'Available' ? hintResult.sources : [])
    .map((s) => ({ source: s, median: s.points.find((p) => p.percentile === 50) }))
    .filter((x): x is { source: typeof x.source; median: NonNullable<typeof x.median> } => x.median !== undefined);
  const hint = hintSources[0];
  const basis = payBasisFromPayType(payType ? Number(payType) : null);
  const rateHelp = (
    <>
      {basis && (
        <span className="aed-line hdc-basis" data-testid="rate-basis-help">
          <strong>{rateLabels[basis]}.</strong> {rateBasisHelp[basis]}
        </span>
      )}
      {band && !quantityPriced && (
        <span className="aed-line" data-testid="employer-band-line">
          Employer pay band for this role: <strong>{formatBandRange(band)}</strong> (a guide only)
        </span>
      )}
      {hint && (
        <span className="aed-line" data-testid="benchmark-hint">
          Market median (reference only): {formatMoney(hint.median.value, hint.source.convertedBasis)} {basisUnit(hint.source.convertedBasis)}, {hint.source.title}, {hint.source.referenceYear}
          {hint.source.kind === 'Synthetic' && <> <span className="aed-badge">{SYNTHETIC_DATA_LABEL}</span></>}
          {hintSources.length > 1 && <> ({hintSources.length - 1} more {hintSources.length === 2 ? 'source is' : 'sources are'} in the role&apos;s Compensation tab.)</>}
        </span>
      )}
      {band && comparison && comparison.position !== 'within' && (
        <span className="aed-warn" role="status" data-testid="band-warning">
          <span aria-hidden="true">{'⚠'} </span>
          This rate is outside the employer pay band for this role: it is {comparison.position === 'below' ? `below the minimum of ${formatMoney(band.minimum, band.basis)}` : `above the maximum of ${formatMoney(band.maximum, band.basis)}`} {basisUnit(band.basis)}.
          {comparison.converted && <> Compared as {formatMoney(comparison.comparedValue, comparison.comparedBasis)} {basisUnit(comparison.comparedBasis)}, using {formatHours(band.annualHoursAssumption)} annual hours.</>}
          {' '}You can still save it.
        </span>
      )}
      {isHealthcare && <span className="aed-line" data-testid="reimbursement-note">{REIMBURSEMENT_SEPARATE_SHORT}</span>}
    </>
  );

  function field(key: FieldKey, label: string, control: (p: Record<string, unknown>) => ReactNode, help?: ReactNode, labelExtra?: ReactNode) {
    const id = `${uid}-${key}`;
    const err = errors[key];
    const describedBy = [err ? `${id}-error` : null, help ? `${id}-help` : null].filter(Boolean).join(' ') || undefined;
    const props = {
      id,
      'data-field': key,
      'aria-required': true,
      'aria-invalid': err ? true : undefined,
      'aria-describedby': describedBy,
      disabled: locked,
    };
    return (
      <div className="aed-field">
        {labelExtra ? (
          <div className="aed-labelrow">
            <label htmlFor={id}>{label} <span aria-hidden="true" className="aed-req">*</span></label>
            {labelExtra}
          </div>
        ) : (
          <label htmlFor={id}>{label} <span aria-hidden="true" className="aed-req">*</span></label>
        )}
        {control(props)}
        {help && <div id={`${id}-help`} className="aed-hint">{help}</div>}
        {err && <p id={`${id}-error`} className="aed-error" role="alert">{err}</p>}
      </div>
    );
  }

  /** EPN-233: the server has created the location. Offer it, and select it if it can be assigned. */
  async function addedLocation(created: WorkLocationResponse) {
    const result = await locationsAfterAdd(created, locations);
    setLocations(result.locations);
    if (result.selectable) {
      setWorkLocationId(created.id);
      clear('workLocationId');
      setLocationNote(`${created.name} was added and selected.`);
    } else {
      setLocationNote(`${created.name} was added but is inactive, so it cannot be assigned yet. Turn on Active for Payroll under Company Details when it is ready.`);
    }
  }

  const clear = (key: FieldKey) => setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

  return (
    <div className="aed-backdrop" role="presentation">
      <div ref={dialogRef} className="aed-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}>
        <div className="aed-head">
          <h3 id={titleId}>Add Employee</h3>
          <button type="button" className="aed-close" aria-label="Close Add Employee" onClick={onClose} disabled={saving}>×</button>
        </div>
        {invitedDone ? (
          <div className="aed-body aed-success" role="status">
            <h4>Invitation sent</h4>
            <p>
              {firstName.trim()} {lastName.trim()} has been invited at {email.trim()}. They will set a password, then complete their own
              details, taxes and documents. You can resend or revoke the invitation from the People list.
            </p>
            <div className="aed-actions">
              <button type="button" className="aed-btn aed-btn--secondary" onClick={() => navigate(`/people/${progress.employeeId}/${EMPLOYEE_PROFILE_STEPS[0].key}`)}>
                Open profile
              </button>
              <button type="button" className="aed-btn aed-btn--primary" onClick={onClose} data-autofocus>Done</button>
            </div>
          </div>
        ) : (
        <form className="aed-body" onSubmit={(e) => void submit('save', e)} noValidate>
          {listsError && <p className="error" role="alert">{listsError}</p>}
          <div className="aed-grid">
            {field('employeeNumber', 'Employee ID', (p) => (
              <input {...p} value={employeeNumber} onChange={(e) => { setEmployeeNumber(e.target.value); clear('employeeNumber'); }} autoComplete="off" />
            ))}
            {field('jobTitleId', 'Role', (p) => (
              <RoleCombobox
                id={p.id as string}
                dataField="jobTitleId"
                roles={rolesForDepartment}
                hasAnyRoles={jobTitles.length > 0}
                departments={departments}
                value={jobTitleId}
                disabled={p.disabled as boolean}
                invalid={Boolean(errors.jobTitleId)}
                describedBy={p['aria-describedby'] as string | undefined}
                required
                onChange={(role) => {
                  setJobTitleId(role?.id ?? '');
                  if (role) {
                    clear('jobTitleId');
                    latest.current = { ...latest.current, jobTitleId: role.id };
                    void suggestFromRole(role);
                  } else {
                    setSuggestionNote('');
                  }
                }}
                onCreateNew={(typed) => { setCreateRoleTitle(typed); setCreateRoleOpen(true); }}
              />
            ), (
              <>
                This is the job role (what this person does). It is not system access and does not change what they can do in EPN.
                {suggestionNote && <span className="aed-note" role="status"> {suggestionNote}</span>}
              </>
            ))}
            {field('firstName', 'First Name', (p) => (
              <input {...p} value={firstName} onChange={(e) => { setFirstName(e.target.value); clear('firstName'); }} autoComplete="off" />
            ))}
            {field('lastName', 'Last Name', (p) => (
              <input {...p} value={lastName} onChange={(e) => { setLastName(e.target.value); clear('lastName'); }} autoComplete="off" />
            ))}
            {field('email', 'Email Address', (p) => (
              <input {...p} type="email" value={email} onChange={(e) => { setEmail(e.target.value); clear('email'); }} autoComplete="off" />
            ))}
            {field('phone', 'Phone Number', (p) => (
              <input {...p} type="tel" placeholder="+1 555 555 0123" value={phone} onChange={(e) => { setPhone(e.target.value); clear('phone'); }} autoComplete="off" />
            ), 'Optional if you invite the employee.')}
            {field('dateOfBirth', 'Date of Birth', (p) => (
              <input {...p} type="date" value={dateOfBirth} onChange={(e) => { setDateOfBirth(e.target.value); clear('dateOfBirth'); clear('hireDate'); }} />
            ), 'Optional if you invite the employee.')}
            {field('hireDate', 'Hiring Date', (p) => (
              <input {...p} type="date" value={hireDate} onChange={(e) => { setHireDate(e.target.value); clear('hireDate'); }} />
            ))}
          </div>
          <hr className="aed-rule" />
          <div className="aed-grid aed-grid--three">
            {field('departmentId', 'Department', (p) => (
              <select
                {...p}
                value={departmentId}
                onChange={(e) => {
                  if (e.target.value === ADD_DEPARTMENT) { setAddDepartmentOpen(true); return; }
                  setDepartmentId(e.target.value); clear('departmentId'); setJobTitleId(''); setSuggestionNote('');
                }}
              >
                <option value="">Select</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                <option value={ADD_DEPARTMENT}>+ Add New Department</option>
              </select>
            ))}
            {field('employmentType', 'Employment Type', (p) => (
              <select {...p} value={employmentType} onChange={(e) => { setEmploymentType(e.target.value); clear('employmentType'); }}>
                <option value="">Select</option>
                {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            ))}
            {field('workLocationId', 'Work Location (Default)', (p) => (
              <select
                {...p}
                value={workLocationId}
                onChange={(e) => {
                  if (e.target.value === ADD_WORK_LOCATION) { setAddLocationOpen(true); return; }
                  setWorkLocationId(e.target.value); clear('workLocationId'); setLocationNote('');
                }}
              >
                <option value="">Select</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                <option value={ADD_WORK_LOCATION}>+ Add New Work Location</option>
              </select>
            ), locationNote ? <span role="status">{locationNote}</span> : undefined)}
          </div>
          <div className="aed-grid">
            <div className="aed-field">
              <div className="aed-labelrow">
                <label htmlFor={`${uid}-discipline`}>Worker category</label>
                <HelpTooltip label="About Worker category" text={TERM_HELP.discipline} />
              </div>
              <DisciplineField
                a11y={{ id: `${uid}-discipline`, 'data-field': 'disciplineId', 'aria-describedby': `${uid}-discipline-hint`, 'aria-invalid': undefined, 'aria-required': undefined }}
                value={disciplineId}
                valueLabel={disciplineName}
                disabled={locked}
                canAdd={caps.discipline?.canManageDisciplines !== false}
                onSelect={(d) => { setDisciplineId(d.id); setDisciplineName(d.name); setDisciplineNote(''); }}
                onClear={() => { setDisciplineId(''); setDisciplineName(''); setDisciplineNote(''); }}
                onAddRequested={(typed) => { setAddDisciplineTyped(typed); setAddDisciplineOpen(true); }}
              />
              <p id={`${uid}-discipline-hint`} className="aed-hint">
                Optional. The kind of worker, like Registered Nurse or Electrician. You can add more worker categories later in the employee&apos;s profile.
                {disciplineNote && <span className="aed-note" role="status"> {disciplineNote}</span>}
              </p>
            </div>
            {field('payType', 'Pay Type', (p) => (
              <select {...p} value={payType} onChange={(e) => { setPayType(e.target.value); clear('payType'); }}>
                <option value="">Select</option>
                {PAY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            ), 'How this person is paid. Per Visit, Per Shift, Daily and Per Unit/Case pay is worked out from quantities entered on a payroll line.',
              <HelpTooltip label="About Pay Type" text={TERM_HELP.payType} />)}
            {field('rate', 'Rate', (p) => (
              <input {...p} inputMode="decimal" placeholder="0.00" value={rate} onChange={(e) => { setRate(e.target.value); clear('rate'); }} autoComplete="off" />
            ), rateHelp, <HelpTooltip label="About Rate" text={isHealthcare ? `${TERM_HELP.rate} ${REIMBURSEMENT_SEPARATE_SHORT}` : TERM_HELP.rate} />)}
            <details className="hdc-explain aed-explain">
              <summary>What do these terms mean?</summary>
              <dl>
                <dt>Worker category</dt><dd>{TERM_HELP.discipline}</dd>
                <dt>Work type</dt><dd>{TERM_HELP.serviceType}</dd>
                <dt>Pay Type</dt><dd>{TERM_HELP.payType}</dd>
                <dt>Rate</dt><dd>{TERM_HELP.rate}</dd>
              </dl>
              {isHealthcare && <p className="hdc-note">{REIMBURSEMENT_SEPARATE_SHORT} It is paid to your company and does not set what you pay an employee.</p>}
            </details>
          </div>

          {formError && <p className="error" role="alert">{formError}</p>}

          <div className="aed-footer">
            <label className="aed-toggle">
              <input
                type="checkbox"
                role="switch"
                checked={isNewHire}
                disabled={locked}
                onChange={(e) => setIsNewHire(e.target.checked)}
              />
              <span>Is this a new hire?</span>
            </label>
            <div className="aed-actions">
              <div className="aed-invite">
                <button
                  type="button"
                  className="aed-btn aed-btn--invite"
                  disabled={saving}
                  aria-describedby={`${uid}-invite-hint`}
                  onClick={() => void submit('invite')}
                >
                  {saving && lastMode === 'invite' ? 'Sending…' : partial && lastMode === 'invite' ? 'Retry invitation' : 'Invite Employee'}
                </button>
                <span id={`${uid}-invite-hint`} className="aed-hint">Employee completes their own details</span>
              </div>
              {partial && (
                <button
                  type="button"
                  className="aed-btn aed-btn--secondary"
                  onClick={() => navigate(`/people/${progress.employeeId}/${EMPLOYEE_PROFILE_STEPS[0].key}`)}
                  disabled={saving}
                >
                  Continue to profile
                </button>
              )}
              <button type="submit" className="aed-btn aed-btn--primary" disabled={saving}>
                {saving && lastMode === 'save' ? 'Saving…' : partial && lastMode === 'save' ? 'Retry' : 'Save & Continue'}
              </button>
            </div>
          </div>
        </form>
        )}
      </div>
      {addDepartmentOpen && (
        <AddDepartmentDialog
          existing={departments}
          onClose={() => setAddDepartmentOpen(false)}
          onAdded={(d, existed) => {
            setDepartments((prev) => (prev.some((x) => x.id === d.id) ? prev : [...prev, d]));
            setDepartmentId(d.id);
            clear('departmentId');
            setJobTitleId('');
            setSuggestionNote(existed ? `${d.name} already exists, so it was selected.` : `${d.name} was added and selected.`);
            setAddDepartmentOpen(false);
          }}
        />
      )}
      {addDisciplineOpen && (
        <AddDisciplineDialog
          initialName={addDisciplineTyped}
          defaultTags={industryTags}
          industryLabel={industryLabel}
          healthcare={isHealthcare}
          onClose={() => setAddDisciplineOpen(false)}
          onAdded={(d: Discipline) => {
            // The new discipline is selected, the rest of the form is untouched, and focus returns to the field (the dialog restores it on close).
            setDisciplineId(d.id);
            setDisciplineName(d.name);
            setDisciplineNote(`${d.name} was added and selected.`);
            setAddDisciplineOpen(false);
          }}
        />
      )}
      {addLocationOpen && (
        <AddWorkLocationDialog
          onClose={() => setAddLocationOpen(false)}
          onAdded={(created) => {
            setAddLocationOpen(false);
            void addedLocation(created);
          }}
        />
      )}
      {createRoleOpen && (
        <CreateRoleDialog
          departments={departments}
          departmentId={departmentId}
          initialTitle={createRoleTitle}
          onClose={() => setCreateRoleOpen(false)}
          onDepartmentAdded={(d) => setDepartments((prev) => (prev.some((x) => x.id === d.id) ? prev : [...prev, d]))}
          onCreated={(role) => {
            setJobTitles((prev) => (prev.some((j) => j.id === role.id) ? prev : [...prev, role]));
            // Only an empty Department is filled in; every other field stays exactly as it was.
            if (!latest.current.departmentId && role.departmentId) {
              setDepartmentId(role.departmentId);
              clear('departmentId');
            }
            latest.current = { ...latest.current, jobTitleId: role.id };
            setJobTitleId(role.id);
            clear('jobTitleId');
            setSuggestionNote('The new role was created and selected.');
            setCreateRoleOpen(false);
          }}
        />
      )}
    </div>
  );
}
